import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "../ui/sidebar";
import { SidebarExpandableTrigger } from "../ui/sidebar-expandable-trigger";
import { NAV_ITEMS } from "../../constants/navigation";
import { CheckCircle } from "lucide-react";
import SystemStatusModal from "../SystemHealth/SystemStatusModal";

export function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, isMobile, setOpenMobile, setOpen } = useSidebar();
  const isCollapsed = state === "collapsed";
  const [isSystemStatusModalOpen, setIsSystemStatusModalOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);
    // Auto-collapse sidebar on navigation for all devices
    if (isMobile) {
      setOpenMobile(false);
    } else {
      setOpen(false);
    }
  };

  return (
    <Sidebar collapsible="icon" data-tour="sidebar">
      <SidebarHeader className="h-[80px] border-b border-gray-200 bg-white">
        <div className={`flex items-center h-full px-3 ${isCollapsed && !isMobile ? 'justify-center' : 'justify-start'}`}>
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center">
              <img src="/SolidIcon.svg" alt="Logo" className="w-10 h-10 object-contain" />
            </div>
            {((!isCollapsed && !isMobile) || (isMobile)) && (
              <span className="text-lg font-bold text-gray-800 tracking-tight ml-6">StackGuard</span>
            )}
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="bg-gradient-to-b from-[#FFFFFF] to-[#F8F9FB] overflow-visible">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {NAV_ITEMS.map((item, index) => {
                const tourId = item.path === '/dashboard' ? 'sidebar-dashboard' :
                              item.path === '/identity-management' ? 'sidebar-integrations' :
                              item.path === '/manage-scans' ? 'sidebar-manage-scans' :
                              item.path === '/settings' ? 'sidebar-settings' : null;
                
                return (
                <SidebarMenuItem key={index}>
                  <SidebarMenuButton
                    data-tour={tourId}
                    onClick={() => handleNavigation(item.path)}
                    isActive={isActive(item.path)}
                    className={`
                      group/item relative overflow-visible h-auto p-3

                      group-data-[collapsible=icon]:!p-3
                      group-data-[collapsible=icon]:!size-auto
                    `}
                    tooltip={isCollapsed ? item.name : null}
                  >
                    <div
                      className={`
                        icon-container flex items-center justify-center rounded-full
                        ${isCollapsed ? "w-10 h-10" : "w-10 h-10"}
                        transition-all duration-200
                        ${isActive(item.path)
                          ? "bg-[#44087D]"
                          : "bg-gray-100 text-gray-600 group-hover/item:bg-[#44087D]/50"
                        }
                      `}
                    >
                      <span className="flex items-center justify-center">
                        {React.cloneElement(
                          isActive(item.path) && item.activeIcon ? item.activeIcon : item.icon,
                          {
                            className: `w-5 h-5 ${isActive(item.path) ? 'brightness-0 invert' : 'group-hover/item:brightness-0 group-hover/item:invert'}`,
                            style: { filter: isActive(item.path) ? 'brightness(0) invert(1)' : undefined }
                          }
                        )}
                      </span>
                    </div>
                    {(!isCollapsed || isMobile) && (
                      <span className="text-sm font-semibold tracking-wide ml-4 group-hover/item:text-[#44087D]">
                        {item.name}
                      </span>
                    )}
                  </SidebarMenuButton>
                </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-3 pb-6 md:pb-3 border-t border-gray-200 bg-white">
        <div className="space-y-3">
          {/* System Health Badge */}
          <div className="flex justify-center">
            <button
              data-tour="system-health"
              onClick={() => setIsSystemStatusModalOpen(true)}
              className={`flex h-8 w-8 items-center justify-center rounded-md bg-green-50 hover:bg-green-100 border border-green-200 transition-all duration-300 ease-in-out hover:shadow-md shadow-sm ${
                !isCollapsed && !isMobile ? "w-full h-8" : ""
              }`}
              aria-label="System Health Status"
            >
              {(!isCollapsed && !isMobile) ? (
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-700" />
                  <span className="text-sm font-medium text-green-700">System Health</span>
                </div>
              ) : (
                <CheckCircle className="w-4 h-4 text-green-700" />
              )}
            </button>
          </div>
          
          {/* Collapse Toggle */}
          <div className="hidden md:flex justify-center">
            <SidebarExpandableTrigger />
          </div>
        </div>
      </SidebarFooter>

      {/* System Status Modal */}
      <SystemStatusModal 
        isOpen={isSystemStatusModalOpen} 
        onClose={() => setIsSystemStatusModalOpen(false)} 
      />
    </Sidebar>
  );
}